<template>
  <div class="field-config">
    <div class="header">
      <h2>字段配置管理</h2>
      <el-button type="primary" @click="showAddDialog">添加字段</el-button>
    </div>

    <!-- 字段列表 -->
    <el-table 
      :data="fieldConfigs" 
      style="width: 100%"
      row-key="id"
      @sort-change="handleSortChange">
      
      <el-table-column prop="displayOrder" label="显示顺序" width="100" sortable="custom">
        <template #default="scope">
          <el-input-number 
            v-model="scope.row.displayOrder" 
            :min="1" 
            :max="999"
            size="small"
            @change="updateDisplayOrder(scope.row)"
          />
        </template>
      </el-table-column>
      
      <el-table-column prop="fieldName" label="字段名" width="150" />
      <el-table-column prop="fieldLabel" label="字段标签" width="150" />
      <el-table-column prop="fieldType" label="字段类型" width="120">
        <template #default="scope">
          <el-tag :type="getFieldTypeColor(scope.row.fieldType || '')">{{ getFieldTypeText(scope.row.fieldType || '') }}</el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="required" label="必填" width="80">
        <template #default="scope">
          <el-switch 
            v-model="scope.row.required" 
            @change="updateFieldConfig(scope.row)"
          />
        </template>
      </el-table-column>
      
      <el-table-column prop="visible" label="可见" width="80">
        <template #default="scope">
          <el-switch 
            v-model="scope.row.visible" 
            @change="updateFieldConfig(scope.row)"
          />
        </template>
      </el-table-column>
      
      <el-table-column prop="defaultValue" label="默认值" width="120" show-overflow-tooltip />
      <el-table-column prop="placeholder" label="占位符" width="120" show-overflow-tooltip />
      <el-table-column prop="validationRules" label="验证规则" width="150" show-overflow-tooltip />
      
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="scope">
          <el-button size="small" @click="editFieldConfig(scope.row)">编辑</el-button>
          <el-button size="small" type="danger" @click="deleteFieldConfig(scope.row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 添加/编辑字段对话框 -->
    <el-dialog 
      :title="dialogTitle" 
      v-model="dialogVisible" 
      width="600px"
      @close="resetForm">
      
      <el-form 
        :model="currentField" 
        :rules="fieldRules" 
        ref="fieldForm" 
        label-width="120px">
        
        <el-form-item label="字段名" prop="fieldName">
          <el-input v-model="currentField.fieldName" placeholder="请输入字段名（英文）" />
        </el-form-item>
        
        <el-form-item label="字段标签" prop="fieldLabel">
          <el-input v-model="currentField.fieldLabel" placeholder="请输入字段标签（中文）" />
        </el-form-item>
        
        <el-form-item label="字段类型" prop="fieldType">
          <el-select v-model="currentField.fieldType" placeholder="请选择字段类型">
            <el-option 
              v-for="type in fieldTypes" 
              :key="type.value" 
              :label="type.label" 
              :value="type.value"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="显示顺序" prop="displayOrder">
          <el-input-number v-model="currentField.displayOrder" :min="1" :max="999" />
        </el-form-item>
        
        <el-form-item label="必填">
          <el-switch v-model="currentField.required" />
        </el-form-item>
        
        <el-form-item label="可见">
          <el-switch v-model="currentField.visible" />
        </el-form-item>
        
        <el-form-item label="默认值">
          <el-input v-model="currentField.defaultValue" placeholder="请输入默认值" />
        </el-form-item>
        
        <el-form-item label="占位符">
          <el-input v-model="currentField.placeholder" placeholder="请输入占位符" />
        </el-form-item>
        
        <el-form-item label="选项" v-if="currentField.fieldType === 'SELECT' || currentField.fieldType === 'RADIO'">
          <el-input 
            v-model="currentField.options" 
            type="textarea" 
            :rows="3"
            placeholder="请输入选项，每行一个"
          />
        </el-form-item>
        
        <el-form-item label="验证规则">
          <el-input 
            v-model="currentField.validationRules" 
            type="textarea" 
            :rows="2"
            placeholder="请输入验证规则（正则表达式）"
          />
        </el-form-item>
        
      </el-form>
      
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="saveFieldConfig">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'FieldConfig',
  data() {
    return {
      fieldConfigs: [],
      dialogVisible: false,
      isEdit: false,
      currentField: {
        fieldName: '',
        fieldLabel: '',
        fieldType: 'TEXT',
        displayOrder: 1,
        required: false,
        visible: true,
        defaultValue: '',
        placeholder: '',
        options: '',
        validationRules: ''
      },
      fieldTypes: [
        { value: 'TEXT', label: '文本' },
        { value: 'NUMBER', label: '数字' },
        { value: 'EMAIL', label: '邮箱' },
        { value: 'PHONE', label: '电话' },
        { value: 'URL', label: '网址' },
        { value: 'TEXTAREA', label: '多行文本' },
        { value: 'SELECT', label: '下拉选择' },
        { value: 'RADIO', label: '单选' },
        { value: 'CHECKBOX', label: '复选框' },
        { value: 'DATE', label: '日期' },
        { value: 'DATETIME', label: '日期时间' },
        { value: 'BOOLEAN', label: '布尔值' }
      ],
      fieldRules: {
        fieldName: [
          { required: true, message: '请输入字段名', trigger: 'blur' },
          { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: '字段名必须以字母开头，只能包含字母、数字和下划线', trigger: 'blur' }
        ],
        fieldLabel: [
          { required: true, message: '请输入字段标签', trigger: 'blur' }
        ],
        fieldType: [
          { required: true, message: '请选择字段类型', trigger: 'change' }
        ],
        displayOrder: [
          { required: true, message: '请输入显示顺序', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? '编辑字段' : '添加字段'
    }
  },
  mounted() {
    this.loadFieldConfigs()
  },
  methods: {
    ...mapActions(['request']),
    
    async loadFieldConfigs() {
      try {
        const response = await this.request({
          url: '/api/admin/field-configs',
          method: 'GET'
        })
        
        if (response.success) {
          this.fieldConfigs = response.data
        } else {
          this.$message.error(response.message || '获取字段配置失败')
        }
      } catch (error) {
        this.$message.error('获取字段配置失败')
      }
    },
    
    showAddDialog() {
      this.isEdit = false
      this.resetForm()
      this.dialogVisible = true
    },
    
    editFieldConfig(field) {
      this.isEdit = true
      this.currentField = { ...field }
      this.dialogVisible = true
    },
    
    async saveFieldConfig() {
      try {
        await this.$refs.fieldForm.validate()
        
        const url = this.isEdit 
          ? `/api/admin/field-configs/${this.currentField.id}`
          : '/api/admin/field-configs'
        const method = this.isEdit ? 'PUT' : 'POST'
        
        const response = await this.request({
          url,
          method,
          data: this.currentField
        })
        
        if (response.success) {
          this.$message.success(response.message || '保存成功')
          this.dialogVisible = false
          this.loadFieldConfigs()
        } else {
          this.$message.error(response.message || '保存失败')
        }
      } catch (error) {
        this.$message.error('保存字段配置失败')
      }
    },
    
    async updateFieldConfig(field) {
      try {
        const response = await this.request({
          url: `/api/admin/field-configs/${field.id}`,
          method: 'PUT',
          data: field
        })
        
        if (response.success) {
          this.$message.success('更新成功')
        } else {
          this.$message.error(response.message || '更新失败')
          this.loadFieldConfigs() // 重新加载数据
        }
      } catch (error) {
        this.$message.error('更新字段配置失败')
        this.loadFieldConfigs()
      }
    },
    
    async updateDisplayOrder(field) {
      await this.updateFieldConfig(field)
    },
    
    async deleteFieldConfig(field) {
      try {
        await this.$confirm('确定要删除这个字段配置吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const response = await this.request({
          url: `/api/admin/field-configs/${field.id}`,
          method: 'DELETE'
        })
        
        if (response.success) {
          this.$message.success('删除成功')
          this.loadFieldConfigs()
        } else {
          this.$message.error(response.message || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除字段配置失败')
        }
      }
    },
    
    resetForm() {
      this.currentField = {
        fieldName: '',
        fieldLabel: '',
        fieldType: 'TEXT',
        displayOrder: this.fieldConfigs.length + 1,
        required: false,
        visible: true,
        defaultValue: '',
        placeholder: '',
        options: '',
        validationRules: ''
      }
      
      if (this.$refs.fieldForm) {
        this.$refs.fieldForm.clearValidate()
      }
    },
    
    getFieldTypeText(type) {
      const typeObj = this.fieldTypes.find(t => t.value === type)
      return typeObj ? typeObj.label : type
    },
    
    getFieldTypeColor(type) {
      const colorMap = {
        'TEXT': '',
        'NUMBER': 'success',
        'EMAIL': 'info',
        'PHONE': 'info',
        'URL': 'info',
        'TEXTAREA': '',
        'SELECT': 'warning',
        'RADIO': 'warning',
        'CHECKBOX': 'warning',
        'DATE': 'danger',
        'DATETIME': 'danger',
        'BOOLEAN': 'success'
      }
      return colorMap[type] || 'info'
    },
    
    handleSortChange({ prop, order }) {
      if (prop === 'displayOrder') {
        if (order === 'ascending') {
          this.fieldConfigs.sort((a, b) => a.displayOrder - b.displayOrder)
        } else if (order === 'descending') {
          this.fieldConfigs.sort((a, b) => b.displayOrder - a.displayOrder)
        }
      }
    }
  }
}
</script>

<style scoped>
.field-config {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  color: #303133;
}

.dialog-footer {
  text-align: right;
}

.el-table {
  margin-top: 20px;
}
</style>